import { Button, color } from "@funded-here-interface/common";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal, Text, Button as MantineButton } from "@mantine/core";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { useStyle } from "../../styles/BorrowerDocUpload";
import { data } from "./Data";
import { File as FileEnum } from "@funded-here-interface/common/src/constant/enum";
import { OnboardingStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import { DocumentsUpload } from "@funded-here-interface/common/src/constant/type";
import FileUploadComponent from "../../components/FileUpload/FileUpload";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import {
  validateForm,
  FormErrors,
} from "@funded-here-interface/common/src/Utils/validateForm";
import {
  getBaseUrl,
  Microservice,
} from "@funded-here-interface/common/src/constant/baseUrl";
import axios from "axios";
import {
  setUserFirstName,
  setUserLastName,
} from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import { useDispatch } from "react-redux";

// Custom hooks and utilities
import { useBorrowerDraftSync } from "./hooks/useBorrowerDraftSync";
import { useBorrowerOrgSubmission } from "./hooks/useBorrowerOrgSubmission";
import {
  extractFilePaths,
  validateRequiredBorrowerDocuments,
  getExistingFilesForBorrowerType,
} from "./utils/documentHelpers";
import {
  setBusinessDoc,
  setProofDoc,
  setShareDoc,
  setBankDoc,
  setBalance,
  setCertificateOfIncorporationDoc,
  setSupportingDocumentsWithSuppliersDoc,
} from "../../features/merchant/doc";

const BorrowerDocsUpload: FC<{
  onDirtyChange?: (isDirty: boolean) => void;
  onSaveDraft?: (callback: () => void) => void;
  onResetForm?: (callback: () => void) => void;
  onSubmit?: () => void;
  errors?: { [key: string]: string };
  onClearError?: (field: string) => void;
}> = ({
  onDirtyChange,
  onSaveDraft,
  onResetForm,
  onSubmit,
  errors,
  onClearError,
}) => {
  const { classes } = useStyle();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // API hooks
  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // Loading and dirty state
  const [isLoading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [shouldMarkDirty, setShouldMarkDirty] = useState(true);
  const [initialDraftData, setInitialDraftData] = useState<any>(null);

  // Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [onboardingStatus, setOnboardingStatus] =
    useState<OnboardingStatusEnum | null>(null);

  // Redux selectors - Auth
  const { token, userEmail, userId, kycStatus, orgId } = useSelector(
    (state: RootState) => state.auth,
  );

  // Redux selectors - Documents
  const {
    business,
    share,
    bank: bankDoc,
    balance,
    proof,
    certificateOfIncorporation,
    supportingDocumentsWithSuppliers,
  } = useSelector((state: RootState) => state.bordoc);

  // Redux selectors - Business, Representative, Bank states
  const borBizState = useSelector((state: RootState) => state.borBiz);
  const borRepState = useSelector((state: RootState) => state.Borrep);
  const borBankState = useSelector((state: RootState) => state.BorBank);

  // Full state for validation
  const state = useSelector((state: RootState) => state);

  // Custom hooks
  const { syncBorrowerDraftToRedux, resetBorrowerDocuments } =
    useBorrowerDraftSync();
  const { submitBorrowerOrganization } = useBorrowerOrgSubmission({
    setLoading,
    onError: (error) => toast.error(error.message),
  });

  const hasFetched = useRef(false);

  /**
   * Load draft data from backend and sync to Redux
   */
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!hasFetched.current && !isLoading && userId) {
        setLoading(true);
        setShouldMarkDirty(false);
        try {
          const data = await getOrgDraftMutation.mutateAsync({
            userId: parseInt(userId),
            token,
          });

          if (data && isMounted) {
            setInitialDraftData(data);
            syncBorrowerDraftToRedux(data);
            setOnboardingStatus(data.onboardingStatus);
            setIsDirty(false);
            onDirtyChange?.(false);
            hasFetched.current = true;
          }
        } catch (error) {
          toast.error(`Error loading draft: ${error}`);
        } finally {
          setLoading(false);
          setShouldMarkDirty(true);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Determine if "Return for Editing" button should be shown
   */
  const isShowStatus =
    onboardingStatus === OnboardingStatusEnum.PENDING_SUBMISSION ||
    onboardingStatus === OnboardingStatusEnum.PENDING_RESUBMISSION;

  /**
   * Handle sending back to CSC for editing
   */
  const handleSendBack = async (
    userId: string,
    onboardingStatus: OnboardingStatusEnum,
  ) => {
    try {
      await axios.post(
        `${getBaseUrl(Microservice.TRADE_DIRECTORY)}/trade-directory/organization/change-status/${userId}`,
        {
          onboardingStatus: onboardingStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success(
        "Onboarding form has been returned to the Client Success Consultant for editing",
      );
    } catch (error) {
      toast.error(
        "Unable to return the onboarding form to your Client Success Consultant for editing. Please reach out to your Client Success Consultant for further help.",
      );
    }
  };

  /**
   * Handle file change for document uploads
   */
  const handleFileChange = (
    fileType: FileEnum,
    files: { path: string; name: string }[],
  ) => {
    const action =
      fileType === FileEnum.CERTIFICATE_OF_INCORPORATION
        ? setCertificateOfIncorporationDoc(files)
        : fileType === FileEnum.PROOF_OF_ADDRESS
          ? setProofDoc(files)
          : fileType === FileEnum.SHAREHOLDERS_DIRECTORS_REGISTERS
            ? setShareDoc(files)
            : fileType === FileEnum.BANK_STATEMENT
              ? setBankDoc(files)
              : fileType === FileEnum.BUSINESS_PROFILE
                ? setBusinessDoc(files)
                : fileType === FileEnum.BALANCE_SHEET
                  ? setBalance(files)
                  : setSupportingDocumentsWithSuppliersDoc(files);
    dispatch(action);
    if (shouldMarkDirty) {
      setIsDirty(true);
      onDirtyChange?.(true);
    }
  };

  /**
   * Save draft to backend
   */
  const saveDraft = async () => {
    return saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token,
        businessProfile: business,
        shareHoldersDirectorsRegisters: share,
        proofOfAddress: proof,
        bankStatement: bankDoc,
        balanceSheetPAndLStatement: balance,
        certificateOfIncorporation,
        supportingDocumentsWithSuppliers,
      },
      {
        onSuccess: () => {
          setIsDirty(false);
          onDirtyChange?.(false);
          toast.success("Draft Saved", { autoClose: 2000 });
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      },
    );
  };

  /**
   * Reset form to initial draft data
   */
  const resetForm = () => {
    resetBorrowerDocuments(initialDraftData);
    setIsDirty(false);
    onDirtyChange?.(false);
  };

  /**
   * Register save draft and reset form callbacks
   */
  useEffect(() => {
    if (onSaveDraft) {
      onSaveDraft(saveDraft);
    }
    if (onResetForm) {
      onResetForm(resetForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveDraft, onResetForm]);

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    saveDraft();
    navigate("/bor-bank");
  };

  /**
   * Handle next - validate and submit organization
   */
  const handleNext = async () => {
    onSubmit?.();

    // Perform validation using validateForm
    const validationErrors: FormErrors = validateForm(state);

    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please fill up all the required fields before submitting.");
      return;
    }

    // Validate all required documents are uploaded
    const missingFiles = validateRequiredBorrowerDocuments({
      businessProfile: business,
      bankStatement: bankDoc,
      proofOfAddress: proof,
      balanceSheetPAndLStatement: balance,
      certificateOfIncorporation,
      supportingDocumentsWithSuppliers,
    });

    if (missingFiles.length > 0) {
      toast.error(`Please upload ${missingFiles.join(", ")}`);
      return;
    }

    saveDraft();

    // Build documents upload object with file paths
    const documentsUpload: DocumentsUpload = {
      proofOfAddress: extractFilePaths(proof),
      businessProfile: extractFilePaths(business),
      shareHoldersDirectorsRegisters: extractFilePaths(share),
      bankStatement: extractFilePaths(bankDoc),
      balanceSheetPAndLStatement: extractFilePaths(balance),
      certificateOfIncorporation: extractFilePaths(certificateOfIncorporation),
      supportingDocumentsWithSuppliers: extractFilePaths(
        supportingDocumentsWithSuppliers,
      ),
    };

    // Update user first/last name in auth state
    dispatch(
      setUserFirstName({
        userFirstName: borRepState.userFirstName ?? null,
      }),
    );
    dispatch(
      setUserLastName({
        userLastName: borRepState.userLastName ?? null,
      }),
    );

    // Submit organization (create or update based on KYC status)
    submitBorrowerOrganization({
      kycStatus: kycStatus || null,
      orgId: orgId?.toString() || null,
      token,
      bizState: borBizState,
      repState: borRepState,
      bankState: borBankState,
      documentsUpload,
    });
  };

  return (
    <>
      <div
        className={classes.stepContent}
        style={{ marginTop: "0px", background: "#fff" }}
      >
        <p className={classes.headinfo}>
          All uploads must be in PDF format. The file size should not exceed
          10MB each.
          <br />
          We may request for additional supporting documents beyond those listed
          here as part of our onboarding and verification process.
        </p>
        {data.map((item) => (
          <FileUploadComponent
            key={item.number}
            number={item.number}
            fileId={item.id}
            FileName={item.heading}
            existingFiles={getExistingFilesForBorrowerType(item.id, {
              businessProfile: business,
              shareholdersDirectorsRegisters: share,
              proofOfAddress: proof,
              bankStatement: bankDoc,
              balanceSheetPAndLStatement: balance,
              certificateOfIncorporation,
              supportingDocumentsWithSuppliers,
            })}
            errors={errors}
            onClearError={onClearError}
            onFileChange={(files) => handleFileChange(item.id, files)}
          />
        ))}

        <div className={classes.buttonRow}>
          <Button
            onClick={handleNext}
            disabled={isLoading}
            textColor={color.WHITE}
            backgroundColor="#338F86"
            children="Submit"
            width="100%"
          />
          {isShowStatus && (
            <Button
              onClick={() => setShowConfirmModal(true)}
              disabled={isLoading}
              textColor="#338F86"
              backgroundColor="#ffff"
              border="1px solid #338F86"
              children="Return for Editing"
              width="100%"
            />
          )}
        </div>
        <div className={classes.buttonWrapper}>
          <Button
            onClick={saveDraft}
            textColor={color.WHITE}
            backgroundColor={color.LOANTEXT}
            children="Save Draft"
            width="100%"
          />
        </div>
      </div>
      <Modal
        opened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Return for Editing"
        styles={{
          title: { fontWeight: 700 },
        }}
      >
        <Text>
          By confirming, a notification will be sent to your Client Success
          Consultant to make the necessary updates. Do you want to proceed?
        </Text>
        <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
          <MantineButton
            onClick={async () => {
              setShowConfirmModal(false);
              // Save draft first to persist any uploaded documents
              // Must await to ensure draft is saved before changing status
              await saveDraft();
              // Determine target status based on current onboarding status
              const targetStatus =
                onboardingStatus === OnboardingStatusEnum.PENDING_RESUBMISSION
                  ? OnboardingStatusEnum.ONBOARDING_RESUBMISSION_IN_DRAFT
                  : OnboardingStatusEnum.ONBOARDING_IN_DRAFT;
              await handleSendBack(userId, targetStatus);
              setOnboardingStatus(targetStatus);
            }}
          >
            Confirm
          </MantineButton>
          <MantineButton
            onClick={() => setShowConfirmModal(false)}
            variant="outline"
          >
            Cancel
          </MantineButton>
        </div>
      </Modal>
    </>
  );
};

export default BorrowerDocsUpload;
